import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { DashboardT } from 'business/definitions';
import { trackEvent } from 'helpers/trackerHelpers';
import { RQAPICallWithData } from 'store/APIClient';
import { useDispatch } from 'store/hooks';
import { useNavigate } from 'react-router-dom';
import { ACTIVE_DASHBOARDS_QUERY_KEY } from './getDashboardsList';

const duplicateDashboard = async (title: string, key: number) => {
    const response = await RQAPICallWithData<DashboardT>({
        endpoint: `explore/dashboard/${key}/duplicate`,
        body: { title },
        method: 'POST',
    });
    return response.content;
};

/**
 * RQ Mutation to duplicate a dashboard.
 * The mutation takes as a parameter a key, that is the id of the dashboard to duplicate,
 * and the title of the new dashboard.
 * @param {(dashboard: DashboardT) => void} onSuccess - A callback function to be executed on success
 */
export const useDuplicateDashboard = (
    onSuccess?: (arg1: DashboardT) => void,
) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    return useMutation({
        mutationFn: ({ title, key }: { title: string; key: number }) => {
            trackEvent('decision/dashboard/options/duplicate-dashboard');
            return duplicateDashboard(title, key);
        },

        onSuccess: ({ data: createdDashboard, message }) => {
            queryClient.setQueryData(
                [ACTIVE_DASHBOARDS_QUERY_KEY],
                (oldDashboards: DashboardT[] | undefined) => [
                    ...(oldDashboards ?? []),
                    createdDashboard,
                ],
            );
            dispatch.vision.notifyUser({
                message,
                snackAction: {
                    message: t('components:common.see'),
                    onClick: () => {
                        navigate(`/dashboard/${createdDashboard.slug}`);
                    },
                },
            });
            if (onSuccess) onSuccess(createdDashboard);
        },
    });
};
